import {
  FocusContext,
  useFocusable,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCMSImageURL } from "../../utils/cms.utils";
import { parseShowcaseTargetURI } from "../showcase/showcase.utils";
import { useInputMode } from "../../utils/hooks/useInputMode";

const ContentRowScrollingWrapper = styled.div`
  overflow: visible;
  flex-shrink: 1;
  flex-grow: 1;
  outline: none !important;
`;

const ContentRowScrollingContent = styled.div`
  display: flex;
  flex-direction: row;
  transition: all ease 0.5s;
  overflow: visible;
`;

const ContentRowWrapper = styled.div`
  position: relative;
  margin-bottom: 5rem;
  overflow: visible;
  min-height: 400px;
`;

const NostalgiaItemWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 20px;
  padding: 20px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all ease 0.2s;
  transform: ${props => props.$active ? "scale(1.25)" : "scale(1)"};
  z-index: ${props => props.$active ? 10 : 1};
  filter: ${props => props.$active ? "drop-shadow(0 0 5rem crimson)" : "none"};
`;

const NostalgiaItem = ({ item = {}, onFocus, isFirst }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const { slug, staticRenderImage } = item || {};
  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () => navigate(parseShowcaseTargetURI(item.actionLink)),
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        setFocus("MENU");
        return false;
      }
      return true;
    },
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    setHovered(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  return (
    <div
      ref={ref}
      onClick={() => navigate(parseShowcaseTargetURI(item.actionLink))}
      className="cursor-pointer flex-auto mx-3"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovered(false)}
    >
      <NostalgiaItemWrapper $active={focused || hovered}>
        <img
          className="w-full"
          alt={slug}
          src={getCMSImageURL(staticRenderImage, {
            width: 720,
            format: "webp",
            quality: 90
          })}
        />
      </NostalgiaItemWrapper>
    </div>
  );
};

export const NostalgiaSlider = ({ staticItems, onFocus, id, index }) => {
  const { ref, focusKey } = useFocusable({
    onFocus,
    focusKey: "ns_" + id,
  });
  const scrollingRef = useRef(null);
  const innerRef = useRef(null);

  const { isMouseFocus } = useInputMode();

  const onAssetFocus = useCallback(
    (props) => {
      if (isMouseFocus) return; // Skip horizontal scroll on mouse hover
      const { x } = props;
      if (x < 0) {
        innerRef.current.style.transform = `translateX(${Math.abs(x)}px)`;
      } else {
        innerRef.current.style.transform = `translateX(0px)`;
      }
    },
    [scrollingRef, isMouseFocus]
  );

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref} tabIndex={index} className="px-[5%] mx-auto">
        <ContentRowScrollingWrapper ref={scrollingRef}>
          <ContentRowScrollingContent ref={innerRef}>
            {staticItems?.map((item, idx) => (
              <NostalgiaItem
                item={item}
                key={"ns_" + id + idx}
                onFocus={onAssetFocus}
                isFirst={idx === 0}
              />
            ))}
          </ContentRowScrollingContent>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
